import { useMemo } from 'react'
import { ProviderTag, SeverityChip, TD, TR } from '../ui'
import { useDevices, useLocationTree, usePersons, useSystems, useZones } from '../../api/hooks'
import { locationNames } from '../../lib/locations'
import { localDate, timeOfDay } from '../../lib/format'
import { EventIcon } from '../EventIcon'
import type { AccessEvent, Severity, SystemProvider } from '../../api/types'

const TYPE_LABELS: Record<string, string> = {
  'access.granted': 'Access granted',
  'access.denied': 'Access denied',
  'intercom.call': 'Intercom call',
  'intercom.call_missed': 'Intercom call missed',
  'io.on': 'IO on',
  'io.off': 'IO off',
}

export function typeLabel(t: string): string {
  return TYPE_LABELS[t] ?? t.replace(/[._-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/** Best human label from the vendor payload when canonical refs are null —
 * the raw event text + the place's door label (techfass composite places are
 * '[connector] [n] - <door label>'), acre's EventDescription + info line. */
export function vendorSummary(e: AccessEvent): { event: string | null; place: string | null; subject: string | null } {
  const raw = e.raw as Record<string, unknown> | null
  if (!raw) return { event: null, place: null, subject: null }
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null)
  let place = str(raw.place)
  if (place) {
    const m = place.match(/^\[[^\]]*\]\s+\[[^\]]*\]\s+-\s+(.+)$/)
    if (m) place = m[1] // composite techfass place → the door label
  }
  const event =
    str(raw.event) ??
    str(raw.EventDescription) ??
    str(raw.message) ??
    str(raw.description)
  const subject = str(raw.subject) ?? str(raw.AdditionalEventInfoString)
  return { event, place, subject }
}

/** The row/modal title: the canonical type label, or -- for an event the
 *  vendor map doesn't know yet -- the vendor's own event text, which is
 *  more useful than a bare "Unknown". */
export function eventTitle(e: AccessEvent): string {
  if (e.type === 'unknown') return vendorSummary(e).event ?? typeLabel(e.type)
  return typeLabel(e.type)
}

/** The row's date line, in the viewer's local time zone -- the same clock as
 *  the `timeOfDay` above it, so the two never disagree around midnight. */
export function dayLine(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return localDate(iso)
}

/** Warning/critical rows get a left edge in their severity hue. */
export const SEVERITY_EDGE: Record<Severity, string> = {
  info: '',
  notice: '',
  warning: 'shadow-[inset_2px_0_0_0_var(--color-ostrel-warn)]',
  critical: 'shadow-[inset_2px_0_0_0_var(--color-ostrel-danger)]',
}

/** The shared event-row resolvers (canonical names + provider tags) and the
 *  context line, built once per consuming surface from the standard queries
 *  (react-query dedupes the fetches). The row's context line: resolved
 *  canonical NAMES (person · zone · location · device — never a bare uid
 *  when a name exists), then any vendor context (event text, place,
 *  subject) that adds information the canonical refs don't already carry. */
export function useEventPresentation() {
  const persons = usePersons()
  const locationTree = useLocationTree()
  const devices = useDevices()
  const zones = useZones()
  const systems = useSystems()

  const personNames = useMemo(
    () => new Map((persons.data ?? []).map((p) => [p.unique_id, p.display_name])),
    [persons.data],
  )
  const locNames = useMemo(() => locationNames(locationTree.data ?? []), [locationTree.data])
  const deviceNames = useMemo(
    () => new Map((devices.data ?? []).map((d) => [d.unique_id, d.name])),
    [devices.data],
  )
  const zoneNames = useMemo(() => new Map((zones.data ?? []).map((z) => [z.unique_id, z.name])), [zones.data])
  const systemProviders = useMemo(
    () => new Map((systems.data ?? []).map((s) => [s.unique_id, s.provider])),
    [systems.data],
  )
  const systemNames = useMemo(
    () => new Map((systems.data ?? []).map((s) => [s.unique_id, s.name])),
    [systems.data],
  )

  const personName = (uid: string | null) => (uid ? (personNames.get(uid) ?? null) : null)
  const locationName = (uid: string | null) => (uid ? (locNames.get(uid) ?? null) : null)
  const deviceName = (uid: string | null) => (uid ? (deviceNames.get(uid) ?? null) : null)
  const zoneName = (uid: string | null) => (uid ? (zoneNames.get(uid) ?? null) : null)
  const systemName = (uid: string | null) => (uid ? (systemNames.get(uid) ?? null) : null)

  const contextLine = (e: AccessEvent): string => {
    const canonical = [personName(e.person), zoneName(e.zone), locationName(e.location), deviceName(e.device)].filter(
      (x): x is string => x !== null,
    )
    const v = vendorSummary(e)
    // An unknown event already shows its vendor text as the title.
    const vendorEvent = e.type === 'unknown' ? null : v.event
    const vendor = [vendorEvent, v.place, v.subject].filter(
      (bit): bit is string => bit !== null && !canonical.some((c) => c.toLowerCase() === bit.toLowerCase()),
    )
    const line = [...canonical, ...vendor].join(' · ')
    if (line) return line
    // No names resolved and no vendor payload (raw is admin-only) — fall
    // back to the raw refs rather than nothing.
    return [e.person, e.zone, e.location, e.device].filter(Boolean).join(' · ') || e.unique_id
  }

  return { contextLine, systemProviders, systemName, personName, locationName, deviceName, zoneName }
}

/** The two-cell row body both variants share: icon + type label over the
 *  context line. */
function EventLabel({ event, contextLine }: { event: AccessEvent; contextLine: (e: AccessEvent) => string }) {
  return (
    <>
      <span className="flex items-center gap-2 text-sm font-medium text-ostrel-ink">
        <EventIcon type={event.type} />
        {eventTitle(event)}
      </span>
      <span className="block truncate pl-6 text-[11px] text-ostrel-ink-muted">{contextLine(event)}</span>
    </>
  )
}

function SystemTag({
  event,
  systemProviders,
}: {
  event: AccessEvent
  systemProviders: Map<string, SystemProvider>
}) {
  if (!event.system) return null
  const provider = systemProviders.get(event.system)
  if (!provider) return <span className="tnum text-[11px] text-ostrel-ink-muted">{event.system}</span>
  return <ProviderTag provider={provider} />
}

/** One event as a TABLE row (the Events page): Time / Event / System /
 *  Severity columns, severity edge, click to open the detail modal. */
export function EventTableRow({
  event,
  onSelect,
}: {
  event: AccessEvent
  onSelect: (e: AccessEvent) => void
}) {
  const { contextLine, systemProviders } = useEventPresentation()
  return (
    <TR className={`cursor-pointer ${SEVERITY_EDGE[event.severity] ?? ''}`} onClick={() => onSelect(event)}>
      <TD>
        <span className="tnum block font-mono text-[11px] text-ostrel-ink">{timeOfDay(event.occurred_at)}</span>
        <span className="tnum block text-[10px] text-ostrel-ink-muted">{dayLine(event.occurred_at)}</span>
      </TD>
      <TD>
        <EventLabel event={event} contextLine={contextLine} />
      </TD>
      <TD>
        <SystemTag event={event} systemProviders={systemProviders} />
      </TD>
      <TD>
        <SeverityChip severity={event.severity} />
      </TD>
    </TR>
  )
}

/** One event as a compact LIST row (dashboard cards): same typography as
 *  the table — time + day, icon + label + context, provider tag, severity
 *  chip, severity edge. Clickable when onSelect is given. */
export function EventListRow({
  event,
  onSelect,
}: {
  event: AccessEvent
  onSelect?: (e: AccessEvent) => void
}) {
  const { contextLine, systemProviders } = useEventPresentation()
  return (
    <li
      className={`flex items-center gap-3 py-2 pl-2 ${SEVERITY_EDGE[event.severity] ?? ''} ${
        onSelect ? 'cursor-pointer' : ''
      }`}
      onClick={onSelect ? () => onSelect(event) : undefined}
    >
      <span className="shrink-0">
        <span className="tnum block font-mono text-[11px] text-ostrel-ink">{timeOfDay(event.occurred_at)}</span>
        <span className="tnum block text-[10px] text-ostrel-ink-muted">{dayLine(event.occurred_at)}</span>
      </span>
      <span className="min-w-0 flex-1">
        <EventLabel event={event} contextLine={contextLine} />
      </span>
      <SystemTag event={event} systemProviders={systemProviders} />
      <SeverityChip severity={event.severity} />
    </li>
  )
}
